import {
  apiService,
  GamePass,
  isValidEntitlement,
  Status,
  SupportedOS,
} from "@blacknut/javascript-sdk/dist";
import { logD, logE } from "@blacknut/logging/dist";
import { Button, MessageModal, ModalSubscription, useModal } from "@blacknut/react-sdk";
import { State } from "core";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import NotSubscribedModal from "src/components/Modals/NotSubscribedModal";
import { useSubscriptionRedirect } from "src/components/SubscriptionRedirect/SubscriptionRedirect";
import { LOGGING_TAG } from "src/utils/Utils";
import { useSignupURL } from "src/utils/useSignupURL";
import React from "react";
import { isInvalidStatus, isTryBuy } from "src/utils/subscription";
import { useWarioURL } from "src/utils/useWarioURL";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import nativeBridge from "src/utils/NativeBridge";
import SwitchToMasterModal from "src/utils/SwitchToMasterModal";
import electronService from "../services/ElectronService";

const useSubscribed = (passId?: string, opts?: { returnUrl?: string }) => {
  const returnUrl = opts ? opts.returnUrl : undefined;
  const { push: modalPush } = useModal();
  const modalSubscription = useRef<ModalSubscription>();
  const { redirectToSubscription, redirectToBrowsePlus } = useSubscriptionRedirect();
  const { user, subscription, organization, config } = useSelector(
    (state: State) => state.globalState,
  );
  const { t } = useTranslation();
  const subStatus = subscription.mainEntitlement?.subscription?.status;
  const isInvalid = isInvalidStatus(subscription.mainEntitlement);
  const isSubscriptionValid = !!(
    (user && isValidEntitlement(subscription.mainEntitlement)) ||
    subStatus === Status.GRACE_PERIOD
  );
  const { profile } = useSelector((state: State) => state.profilesState);
  const _isTryBuy = isTryBuy(subscription.mainEntitlement);
  const [amzInited, setAmzInited] = useState(false);
  const [amzPaymentAvailable, setAmzPaymentAvailable] = useState(false);
  const showModal =
    amzPaymentAvailable || config?.features?.noDynamicPricing || isInvalid;

  const { createSubURL } = useSignupURL({
    ...(passId && { uuid: passId, bundle: `${!isSubscriptionValid || _isTryBuy}` }),
  });
  const { create } = useWarioURL();
  const location = useLocation();

  const getWarioURL = useCallback(async () => {
    const warioUrl = await create(organization?.subscriptionUrl, undefined, {
      tokens: profile?.isMaster || false,
    });
    return warioUrl.toString();
  }, [create, organization?.subscriptionUrl, profile?.isMaster]);

  const getSubURL = useCallback(async () => {
    const subURL = await createSubURL();
    return subURL.toString();
  }, [createSubURL]);

  const _redirectToSubscription = useCallback(async () => {
    modalSubscription.current?.remove();
    modalSubscription.current = undefined;

    if (isInvalid) {
      const warioUrl = await getWarioURL();
      setTimeout(() => {
        if (electronService.isAvailable()) {
          window.open(warioUrl, "_blank");
        } else {
          window.location.replace(warioUrl);
        }
      }, 500);
    } else {
      //Le's defer redirect, otherwise, redirect will go the / but modal close will go back
      //Stream will be relaunched
      setTimeout(
        config?.features?.noDynamicPricing
          ? redirectToSubscription
          : redirectToBrowsePlus,
        500,
      );
    }
  }, [
    config?.features?.noDynamicPricing,
    getWarioURL,
    isInvalid,
    redirectToBrowsePlus,
    redirectToSubscription,
  ]);

  const _redirectToBundleOffer = useCallback(async () => {
    modalSubscription.current?.remove();
    modalSubscription.current = undefined;

    //Le's defer redirect, otherwise, redirect will go the / but modal close will go back
    //Stream will be relaunched
    const subURL = await getSubURL();
    setTimeout(() => {
      if (electronService.isAvailable()) {
        window.open(subURL, "_blank");
      } else {
        window.location.replace(subURL);
      }
    }, 500);
  }, [getSubURL]);

  useEffect(() => {
    const init = async () => {
      if (amzInited) return;
      if (
        apiService.userAgent.os === SupportedOS.Fireos &&
        config?.features?.amazonPayment &&
        nativeBridge.module?.handleInAppSubscribe &&
        nativeBridge.module?.canHandleInAppSubscribe
      ) {
        const res = await nativeBridge.module.canHandleInAppSubscribe();
        setAmzInited(true);
        setAmzPaymentAvailable(res);
      } else {
        setAmzInited(true);
        setAmzPaymentAvailable(false);
      }
    };
    init();
  }, [amzInited, config?.features?.amazonPayment]);

  const closeModal = useCallback(() => {
    modalSubscription.current?.remove();
    modalSubscription.current = undefined;
  }, []);

  const onModalError = useCallback(
    (s: string) => {
      logE(LOGGING_TAG, "Caught error in modal: %o", s);
      modalSubscription.current?.remove({ preventGoBack: true });
      modalSubscription.current = modalPush((props) => {
        return (
          <MessageModal
            {...props}
            message={t("errors.generic")}
            title={t("dialogs.error.title")}
            onClose={closeModal}
            testID="errorModal"
            buttons={[
              <Button key="ok" onClick={closeModal}>
                {t("buttons.ok")}
              </Button>,
            ]}
          />
        );
      });
    },
    [closeModal, modalPush, t],
  );

  const checkForValidSubscription = async (
    gameId: string,
    gamePass?: GamePass,
    forceModal = true,
  ) => {
    const isInvalidGracePeriod = subStatus === Status.GRACE_PERIOD && gamePass;

    if (!isSubscriptionValid || (_isTryBuy && gamePass) || isInvalidGracePeriod) {
      logD(LOGGING_TAG, "Current subscription is not valid");

      const isProfileMaster = profile?.isMaster || false;
      const redirectUrl = returnUrl ? returnUrl : location.pathname;

      if (!isProfileMaster) {
        modalSubscription.current = modalPush((props) => (
          <SwitchToMasterModal
            {...props}
            redirectUrl={redirectUrl}
            closeModal={closeModal}
          />
        ));
        return isSubscriptionValid && !isInvalidGracePeriod;
      }

      if (showModal || forceModal) {
        const subURL = await getSubURL();
        modalSubscription.current = modalPush((props) => (
          <NotSubscribedModal
            {...props}
            isProfileMaster={isProfileMaster}
            subscribeURL={gamePass && !isInvalid ? subURL : undefined}
            onSubscribe={
              gamePass && !isInvalid ? _redirectToBundleOffer : _redirectToSubscription
            }
            onClose={closeModal}
            onPlanSubscribed={closeModal}
            gamePass={gamePass}
            redirectUrl={redirectUrl}
            gameId={gameId}
            onError={onModalError}
          />
        ));
      } else {
        redirectToBrowsePlus();
      }
    }

    return isSubscriptionValid && !isInvalidGracePeriod;
  };

  const checkForTrialSubscription = (forceModal = true) => {
    if (_isTryBuy) {
      logD(LOGGING_TAG, "Current subscription is trialing");

      if (showModal || forceModal) {
        modalSubscription.current = modalPush((props) => (
          <NotSubscribedModal
            {...props}
            isProfileMaster={profile?.isMaster || false}
            onSubscribe={_redirectToSubscription}
            onClose={closeModal}
            onPlanSubscribed={closeModal}
            redirectUrl={location.pathname}
            gameId={""}
            onError={onModalError}
          />
        ));
      } else {
        redirectToBrowsePlus();
      }
    }

    return !_isTryBuy;
  };

  return {
    checkForValidSubscription,
    checkForTrialSubscription,
    mainEntitlement: subscription.mainEntitlement,
    amzPaymentAvailable,
  };
};

export default useSubscribed;
